import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, AlertCircle } from 'lucide-react';
import sabotagelogo from '@/assets/sabotage-logo.png';
import { consumeAuthCallback, type AuthCallbackErrorKind } from '@/lib/authCallback';
import { DEFAULT_AUTHED_HOME } from '@/lib/landingAuthCopy';
import { safeNextPath } from '@/lib/safeRedirect';

// Landing route for token_hash confirmation links (confirm signup, change email).
// It calls verifyOtp via consumeAuthCallback, then forwards to `next` (which carries
// ?intent=), so the intent survives even a cross-device confirmation. Backward
// compatible: old ?code= / #access_token links that happen to land here are handled
// by the same helper.

const ERROR_COPY: Record<AuthCallbackErrorKind, { title: string; body: string }> = {
  expired: {
    title: 'This link has expired',
    body: 'Email links are only valid for a short time. Request a new one and try again.',
  },
  used: {
    title: 'This link was already used',
    body: 'This confirmation link has already been used. If you are not signed in, sign in normally.',
  },
  invalid: {
    title: 'This link is invalid',
    body: 'We could not verify this link. Request a new one and try again.',
  },
  unknown: {
    title: 'We could not confirm this link',
    body: 'Something went wrong verifying this link. Request a new one and try again.',
  },
};

// `safeNextPath` (the last line of defense against an open redirect in this flow)
// lives in @/lib/safeRedirect alongside safeReturnToPath, and is unit-tested there.

export default function AuthConfirm() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [errorKind, setErrorKind] = useState<AuthCallbackErrorKind | null>(null);
  const nextRaw = params.get('next');

  useEffect(() => {
    let active = true;
    (async () => {
      const result = await consumeAuthCallback();
      if (!active) return;
      if (result.status === 'success') {
        navigate(safeNextPath(nextRaw) ?? DEFAULT_AUTHED_HOME, { replace: true });
      } else if (result.status === 'error') {
        setErrorKind(result.errorKind ?? 'unknown');
      } else {
        // No token in the URL at all.
        setErrorKind('invalid');
      }
    })();
    return () => { active = false; };
  }, [navigate, nextRaw]);

  const copy = errorKind ? ERROR_COPY[errorKind] : null;

  return (
    <div className="min-h-screen bg-gradient-dark flex items-center justify-center px-4">
      <Card className="w-full max-w-md border-border bg-card">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <img src={sabotagelogo} alt="Sabotage Fantasy" className="h-20 w-auto" />
          </div>
          <CardTitle className="text-2xl font-bold text-foreground font-display">
            {copy ? copy.title : 'Confirming your email...'}
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            {copy ? copy.body : 'One moment while we verify your link.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!copy ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="text-center space-y-4">
              <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
              <Button asChild className="w-full">
                <Link to="/login">Back to Sign In</Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
